import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CalendarClock, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Compact reminder card for the admin dashboard — shows any leave that starts
 * within the next 14 days so it's never a surprise. Clicking a row opens the
 * full Leave Manager.
 */
export default function UpcomingLeavesCard({ days = 14 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API}/leave/upcoming-reminders?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setItems(r.data.upcoming || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || items.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/50" data-testid="upcoming-leaves-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-amber-600" />
          Upcoming Staff Leave (next {days} days)
        </CardTitle>
        <Link to="/admin/leave">
          <Button variant="outline" size="sm" className="h-8">
            Manage <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.slice(0, 6).map((r) => (
          <div key={r.id} className="flex items-center justify-between bg-white rounded-md border px-3 py-2 text-sm">
            <div className="min-w-0">
              <div className="font-medium truncate">{r.staff_name}</div>
              <div className="text-[11px] text-muted-foreground capitalize">
                {r.leave_type.replace(/_/g, ' ')} · {r.start_date} → {r.end_date} · {r.days} day{r.days !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge
                variant="outline"
                className={
                  r.status === 'approved'
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                    : 'bg-amber-100 text-amber-800 border-amber-300'
                }
              >
                {r.status}
              </Badge>
              <span className="text-xs text-slate-600">
                {r.days_until_start === 0 ? 'today' : `in ${r.days_until_start} day${r.days_until_start !== 1 ? 's' : ''}`}
              </span>
            </div>
          </div>
        ))}
        {items.length > 6 && (
          <div className="text-xs text-muted-foreground text-center pt-1">
            + {items.length - 6} more — see the Leave Manager for the full list.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
